<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="isEditing ? 'Edit Contact' : 'Create New Contact'"
    :modal="true"
    :closable="false"
    :style="{ width: '80vw', maxWidth: '900px' }"
    :breakpoints="{ '960px': '100vw' }"
  >
    <Tabs value="0">
      <TabList>
        <Tab value="0">Basic Information</Tab>
        <Tab value="1">Documents</Tab>
        <Tab value="2">Additional Information</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <div class="grid">
            <!-- Basic Info Fields -->
            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="firstName" v-model="person.firstName" class="w-full" />
                <label for="firstName">First Name*</label>
              </span>
              <small v-if="submitted && !person.firstName" class="p-error">
                First name is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="lastName" v-model="person.lastName" class="w-full" />
                <label for="lastName">Last Name*</label>
              </span>
              <small v-if="submitted && !person.lastName" class="p-error">
                Last name is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="middleName" v-model="person.middleName" class="w-full" />
                <label for="middleName">Middle Name</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="idNumber" v-model="person.idNumber" class="w-full" />
                <label for="idNumber">ID #</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <Select
                  id="personType"
                  v-model="person.type"
                  :options="personTypes"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="personType">Contact Type*</label>
              </span>
              <small v-if="submitted && !person.type" class="p-error">
                Contact type is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <Select
                  id="status"
                  v-model="person.status"
                  :options="statusOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="status">Status*</label>
              </span>
              <small v-if="submitted && !person.status" class="p-error">
                Status is required.
              </small>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="jobTitle" v-model="person.jobTitle" class="w-full" />
                <label for="jobTitle">Job Title</label>
              </span>
            </div>

            <!-- Organization Selection -->
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <AutoComplete
                  id="organization"
                  v-model="selectedOrganization"
                  :suggestions="filteredOrganizations"
                  @complete="searchOrganizations"
                  @item-select="onOrganizationSelect"
                  field="name"
                  dropdown
                  class="w-full"
                  optionLabel="name"
                />
                <label for="organization">Company</label>
              </span>
            </div>

            <!-- Contact Information Fields -->
            <div class="col-12">
              <h3 class="text-lg font-medium mb-2 mt-3">Contact Information</h3>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="email" v-model="person.email" class="w-full" />
                <label for="email">Email</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputMask id="phone" v-model="person.phone" mask="(999) 999-9999" class="w-full" />
                <label for="phone">Phone</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputMask
                  id="mobilePhone"
                  v-model="person.mobilePhone"
                  mask="(999) 999-9999"
                  class="w-full"
                />
                <label for="mobilePhone">Mobile Phone</label>
              </span>
            </div>

            <!-- Address Fields -->
            <div class="col-12">
              <h3 class="text-lg font-medium mb-2 mt-3">Address</h3>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="streetAddress" v-model="person.streetAddress" class="w-full" />
                <label for="streetAddress">Street Address</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="city" v-model="person.city" class="w-full" />
                <label for="city">City</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="state" v-model="person.state" class="w-full" />
                <label for="state">State/Province</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="postalCode" v-model="person.postalCode" class="w-full" />
                <label for="postalCode">Postal Code</label>
              </span>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <span class="p-float-label">
                <InputText id="country" v-model="person.country" class="w-full" />
                <label for="country">Country</label>
              </span>
            </div>
          </div>
        </TabPanel>

        <!-- Documents Tab -->
        <TabPanel value="1">
          <div class="grid">
            <div class="col-12 mb-4">
              <h3 class="text-lg font-medium mb-2">Identification Documents</h3>
              <div class="p-field">
                <label for="idDocument" class="block mb-2">ID Document</label>
                <FileUpload
                  id="idDocument"
                  mode="basic"
                  accept="image/*,application/pdf"
                  :maxFileSize="5000000"
                  chooseLabel="Upload ID Document"
                  class="w-full"
                  @select="onIdDocumentSelect"
                />
                <small class="text-gray-500">Upload driver's license, passport, or other ID</small>
                <div v-if="person.photoIdPreview || person.photoIdUrl" class="mt-2">
                  <img
                    v-if="person.photoIdPreview || person.photoIdUrl"
                    :src="person.photoIdPreview || person.photoIdUrl"
                    class="border-round shadow-2"
                    style="max-height: 150px"
                  />
                  <div v-else class="flex align-items-center">
                    <i class="pi pi-file-pdf text-red-500 text-xl mr-2"></i>
                    <span>PDF Document</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Other Documents section -->
            <div class="col-12">
              <h3 class="text-lg font-medium mb-2">Other Documents</h3>

              <!-- File Upload -->
              <div class="mb-3">
                <FileUpload
                  mode="basic"
                  accept="image/*,application/pdf"
                  :maxFileSize="5000000"
                  chooseLabel="Add Document"
                  class="w-full"
                  @select="onAttachmentSelect"
                />
              </div>

              <!-- Attachments List -->
              <div v-if="person.attachments && person.attachments.length > 0" class="grid">
                <div
                  v-for="attachment in person.attachments"
                  :key="attachment.id"
                  class="col-12 mb-3"
                >
                  <div class="surface-card p-3 border-round shadow-1">
                    <div class="flex align-items-center justify-content-between">
                      <div class="flex align-items-center">
                        <template
                          v-if="attachment.fileType && attachment.fileType.startsWith('image/')"
                        >
                          <img
                            :src="attachment.url"
                            alt=""
                            class="mr-2 border-round"
                            style="width: 40px; height: 40px; object-fit: cover"
                          />
                        </template>
                        <template v-else>
                          <i :class="getFileIcon(attachment.fileType)" class="mr-2"></i>
                        </template>
                        <div>
                          <div class="font-medium">{{ attachment.name }}</div>
                          <div class="text-sm text-500">
                            {{ attachment.category }} • Added
                            {{ formatDate(attachment.uploadedAt) }}
                          </div>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <Button
                          icon="pi pi-eye"
                          class="p-button-rounded p-button-text"
                          @click="viewAttachment(attachment)"
                        />
                        <Button
                          icon="pi pi-trash"
                          class="p-button-rounded p-button-text p-button-danger"
                          @click="removeAttachment(attachment)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-500 text-center p-3">No additional documents uploaded</div>
            </div>
          </div>

          <!-- Cropper Dialog -->
          <Dialog
            v-model:visible="showCropperDialog"
            :header="'Crop ID Document'"
            :modal="true"
            :closable="false"
            :style="{ width: '90vw', maxWidth: '600px' }"
            :breakpoints="{ '960px': '90vw' }"
          >
            <div class="cropper-container">
              <Cropper
                v-if="cropperImage"
                class="cropper"
                :src="cropperImage"
                :stencil-props="{
                  aspectRatio: 5 / 3,
                }"
                image-restriction="stencil"
                :canvas="{
                  width: 600,
                  height: 400,
                }"
                ref="cropperRef"
              />

              <div class="flex justify-content-center mt-3">
                <Button
                  icon="pi pi-refresh"
                  class="p-button-rounded p-button-outlined mr-2"
                  @click="rotateImage(-90)"
                  aria-label="Rotate Left"
                />
                <Button
                  icon="pi pi-refresh"
                  class="p-button-rounded p-button-outlined"
                  style="transform: scaleX(-1)"
                  @click="rotateImage(90)"
                  aria-label="Rotate Right"
                />
              </div>
            </div>
            <template #footer>
              <Button
                label="Cancel"
                icon="pi pi-times"
                class="p-button-text mr-2"
                @click="cancelCrop"
              />
              <Button label="Crop" icon="pi pi-check" @click="cropImage" />
            </template>
          </Dialog>

          <!-- Add Document Dialog -->
          <Dialog
            v-model:visible="showAddAttachmentDialog"
            header="Add Document"
            :modal="true"
            :closable="false"
            :style="{ width: '450px' }"
          >
            <div class="grid">
              <div class="col-12 mb-3">
                <span class="p-float-label">
                  <InputText id="documentName" v-model="newAttachment.name" class="w-full" />
                  <label for="documentName">Document Name*</label>
                </span>
              </div>
              <div class="col-12 mb-3">
                <span class="p-float-label">
                  <Select
                    id="documentCategory"
                    v-model="newAttachment.category"
                    :options="documentCategories"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                  />
                  <label for="documentCategory">Category*</label>
                </span>
              </div>
              <div class="col-12 mb-3">
                <span class="p-float-label">
                  <Calendar
                    id="expirationDate"
                    v-model="newAttachment.expiresAt"
                    dateFormat="mm/dd/yy"
                    class="w-full"
                  />
                  <label for="expirationDate">Expiration Date (Optional)</label>
                </span>
              </div>
              <div class="col-12">
                <span class="p-float-label">
                  <Textarea
                    id="documentNotes"
                    v-model="newAttachment.notes"
                    rows="3"
                    class="w-full"
                  />
                  <label for="documentNotes">Notes (Optional)</label>
                </span>
              </div>
            </div>
            <template #footer>
              <Button
                label="Cancel"
                icon="pi pi-times"
                class="p-button-text"
                @click="cancelAddAttachment"
              />
              <Button label="Save" icon="pi pi-check" @click="saveAttachment" />
            </template>
          </Dialog>
        </TabPanel>

        <!-- Additional Information Tab -->
        <TabPanel value="2">
          <div class="grid">
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <Textarea id="notes" v-model="person.notes" rows="5" class="w-full" />
                <label for="notes">Notes</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputText id="referredBy" v-model="person.referredBy" class="w-full" />
                <label for="referredBy">Referred By</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <span class="p-float-label">
                <InputChips v-model="person.tags" class="w-full" />
                <label for="tags">Tags</label>
              </span>
            </div>

            <div class="col-12 mb-3">
              <label for="rating" class="block mb-2">Rating</label>
              <Rating v-model="person.rating" :cancel="false" />
            </div>

            <div class="col-12 md:col-6 mb-3">
              <div class="flex align-items-center">
                <ToggleSwitch v-model="person.isVerified" />
                <label for="isVerified" class="ml-2">Verified</label>
              </div>
            </div>

            <div class="col-12 md:col-6 mb-3">
              <div class="flex align-items-center">
                <ToggleSwitch v-model="person.isExempt" />
                <label for="isExempt" class="ml-2">Tax Exempt</label>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text mr-2" @click="cancel" />
      <Button
        :label="isEditing ? 'Update' : 'Save'"
        icon="pi pi-check"
        @click="save"
        :loading="saving"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { compressImage } from '@/utils/imageHelper'
import { uploadImage } from '@/utils/imageService'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useOrganizationStore } from '@/stores/v2/organization'
import { createPerson } from '@/factories/personFactory'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'

// PrimeVue components
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputMask from 'primevue/inputmask'
import Select from 'primevue/select'
import Calendar from 'primevue/calendar'

import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import InputChips from 'primevue/inputchips'
import Rating from 'primevue/rating'
import FileUpload from 'primevue/fileupload'
import AutoComplete from 'primevue/autocomplete'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  personData: {
    type: Object,
    default: () => ({
      type: 'customer',
      status: 'active',
      isVerified: false,
      isExempt: false,
      tags: [],
    }),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:visible', 'person-saved', 'cancel'])

const toast = useToast()
const confirm = useConfirm()
const organizationStore = useOrganizationStore()
const dialogVisible = ref(props.visible)
const person = reactive<Partial<Person>>(createPerson({ ...props.personData }))
const submitted = ref(false)
const saving = ref(false)

const cropperRef = ref<any>(null)
const showCropperDialog = ref(false)
const currentCropFile = ref<File | null>(null)
const currentCropCallback = ref<((file: File) => void) | null>(null)
const cropperImage = ref<string | null>(null)

// Organization selection
const selectedOrganization = ref<Organization | null>(null)
const filteredOrganizations = ref<Organization[]>([])

// Person type options
const personTypes = [
  { label: 'Supplier', value: 'supplier' },
  { label: 'Customer', value: 'customer' },
  { label: 'Both', value: 'both' },
]

// Status options
const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Blacklisted', value: 'blacklisted' },
]

// Add new refs for attachment handling
const showAddAttachmentDialog = ref(false)
const currentFile = ref<File | null>(null)
const newAttachment = ref({
  id: '',
  name: '',
  fileType: '',
  url: '',
  category: 'other' as const,
  uploadedAt: new Date(),
  expiresAt: undefined as Date | undefined,
  notes: '',
})

const documentCategories = [
  { label: 'Tax Form', value: 'taxForm' },
  { label: 'Business License', value: 'businessLicense' },
  { label: 'Certification', value: 'certification' },
  { label: 'Contract', value: 'contract' },
  { label: 'Other', value: 'other' },
]

// Watch for changes in props
watch(
  () => props.visible,
  (newValue) => {
    dialogVisible.value = newValue
  },
)

watch(
  () => props.personData,
  async (newValue) => {
    Object.assign(person, createPerson(newValue))

    // Resolve selected organization using v2 store (no direct items access)
    if (person.organizationId) {
      const org = await organizationStore.get(person.organizationId)
      selectedOrganization.value = org ?? null
    } else {
      selectedOrganization.value = null
    }
  },
)

watch(dialogVisible, (newValue) => {
  emit('update:visible', newValue)
})

// Save the person
async function save() {
  submitted.value = true

  // Validate required fields
  if (!person.firstName || !person.lastName || !person.type || !person.status) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  saving.value = true

  try {
    // Upload photo ID if it exists
    if (person.photoIdFile) {
      const path = `person_documents/${person.id}/photo_id`
      person.photoIdUrl = await uploadImage(person.photoIdFile, path)
      delete person.photoIdFile
      delete person.photoIdPreview
    }

    // Create a copy of the person object without any reactive wrappers
    const personToSave = { ...person }

    // Emit the saved person
    emit('person-saved', personToSave)

    // Close the dialog
    dialogVisible.value = false
    submitted.value = false
  } catch (error) {
    console.error(`Error ${props.isEditing ? 'updating' : 'creating'} person:`, error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to ${props.isEditing ? 'update' : 'create'} person`,
      life: 3000,
    })
  } finally {
    saving.value = false
  }
}

// Cancel and close the dialog
function cancel() {
  dialogVisible.value = false
  submitted.value = false
  emit('cancel')
}

// Removed barcode scanner integration; ID scanning is handled in ticket intake only

// Document handling functions
async function onIdDocumentSelect(event: { files: File[] }) {
  const file = event.files[0]
  if (!file) return

  // Check if it's an image file that can be cropped
  if (file.type.startsWith('image/')) {
    // Open the cropper dialog
    openCropperDialog(file, async (croppedFile: File) => {
      try {
        // Compress the cropped image
        const compressedFile = await compressImage(croppedFile)
        person.photoIdFile = compressedFile

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          if (e.target?.result) {
            person.photoIdPreview = e.target.result as string
          }
        }
        reader.readAsDataURL(compressedFile)
      } catch (error) {
        console.error('Error processing ID document:', error)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process document',
          life: 3000,
        })
      }
    })
  } else {
    // For non-image files (like PDFs), use the original file
    person.photoIdFile = file

    // Create preview (will show as PDF icon)
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        person.photoIdPreview = e.target.result as string
      }
    }
    reader.readAsDataURL(file)
  }
}

// Function to open the Cropper dialog
function openCropperDialog(file: File, callback: (file: File) => void) {
  currentCropFile.value = file
  currentCropCallback.value = callback

  // Create a URL for the image
  const reader = new FileReader()
  reader.onload = (e) => {
    if (e.target?.result) {
      cropperImage.value = e.target.result as string
      showCropperDialog.value = true
    }
  }
  reader.readAsDataURL(file)
}

// Function to crop the image
async function cropImage() {
  if (cropperRef.value) {
    try {
      const { canvas } = (cropperRef.value as any).getResult()

      // Convert canvas to blob
      canvas.toBlob(
        async (blob: Blob | null) => {
          if (blob && currentCropFile.value) {
            // Create a File object from the blob
            const croppedFile = new File([blob], currentCropFile.value.name, {
              type: 'image/jpeg',
              lastModified: new Date().getTime(),
            })

            // Call the callback with the cropped file
            if (currentCropCallback.value) {
              currentCropCallback.value(croppedFile)
            }

            showCropperDialog.value = false
            cropperImage.value = null
          }
        },
        'image/jpeg',
        0.9,
      )
    } catch (error) {
      console.error('Error cropping image:', error)
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to crop image',
        life: 3000,
      })
    }
  }
}

// Function to cancel cropping
function cancelCrop() {
  showCropperDialog.value = false
  currentCropFile.value = null
  currentCropCallback.value = null
  cropperImage.value = null
}

function rotateImage(degrees: number) {
  if (cropperRef.value) {
    ;(cropperRef.value as any).rotate(degrees)
  }
}

// Search organizations
async function searchOrganizations(event: { query: string }) {
  const query = (event.query || '').toLowerCase().trim()
  if (!query) {
    filteredOrganizations.value = []
    return
  }
  try {
    const search = await organizationStore.search()
    const results = await search.prefixName(query, 10).run()
    filteredOrganizations.value = results
  } catch (e) {
    console.error('Error searching organizations:', e)
    filteredOrganizations.value = []
  }
}

// Handle organization selection
function onOrganizationSelect(event: { value: Organization }) {
  person.organizationId = event.value.id
}

// Add new methods for attachment handling
async function onAttachmentSelect(event: { files: File[] }) {
  const file = event.files[0]
  if (!file) return

  // Store the file temporarily
  currentFile.value = file
  newAttachment.value = {
    id: crypto.randomUUID(),
    name: file.name,
    fileType: file.type,
    url: '',
    category: 'other' as const,
    uploadedAt: new Date(),
    expiresAt: undefined,
    notes: '',
  }

  // Show the dialog to collect additional information
  showAddAttachmentDialog.value = true
}

async function saveAttachment() {
  try {
    if (!currentFile.value) {
      throw new Error('No file selected')
    }

    // Upload the file
    const path = `person_documents/${person.id}/attachments/${newAttachment.value.id}`
    const url = await uploadImage(currentFile.value, path)

    // Add to attachments array
    if (!person.attachments) {
      person.attachments = []
    }

    person.attachments.push({
      ...newAttachment.value,
      url,
      category: newAttachment.value.category as
        | 'taxForm'
        | 'businessLicense'
        | 'certification'
        | 'contract'
        | 'other',
      expiresAt: newAttachment.value.expiresAt ?? undefined,
    })

    showAddAttachmentDialog.value = false
    currentFile.value = null
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Document added successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Error saving attachment:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save document',
      life: 3000,
    })
  }
}

function cancelAddAttachment() {
  showAddAttachmentDialog.value = false
  currentFile.value = null
  newAttachment.value = {
    id: '',
    name: '',
    fileType: '',
    url: '',
    category: 'other' as const,
    uploadedAt: new Date(),
    expiresAt: undefined,
    notes: '',
  }
}

function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return 'pi pi-image text-blue-500'
  if (fileType === 'application/pdf') return 'pi pi-file-pdf text-red-500'
  return 'pi pi-file text-gray-500'
}

function formatDate(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

function viewAttachment(attachment: { url: string }) {
  window.open(attachment.url, '_blank')
}

function removeAttachment(attachment: { id: string }) {
  confirm.require({
    message: 'Are you sure you want to remove this document?',
    header: 'Confirm Removal',
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      if (person.attachments) {
        person.attachments = person.attachments.filter((a) => a.id !== attachment.id)
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Document removed successfully',
          life: 3000,
        })
      }
    },
  })
}

onMounted(async () => {
  // Resolve selected organization if present
  if (person.organizationId) {
    const org = await organizationStore.get(person.organizationId)
    if (org) {
      selectedOrganization.value = org
    }
  }
})

onBeforeUnmount(() => {})
</script>

<style scoped>
.p-tabview-panels {
  padding: 1.5rem 0;
}

.cropper-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0;
}

.cropper {
  height: 400px;
  width: 100%;
  background: #f8f8f8;
}

/* Scanner removed from PersonDialog */
</style>
